import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RotateCcw, History } from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { AdsPhaseCard } from './AdsPhaseCard';
import { checklistsAPI } from '../lib/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : 'inicio');
const monthName = (m) => { if (!m) return ''; const s = new Date(`${m}-01T00:00:00`).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }); return s.charAt(0).toUpperCase() + s.slice(1); };

const HistoryDialog = ({ entries, onClose, title = 'Ciclos anteriores' }) => (
  <Dialog open={!!entries} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ads-history-dialog">
      <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      {!entries?.length ? <p className="text-sm text-muted-foreground py-6 text-center">Todavía no se ha cerrado ningún ciclo.</p> : entries.map((h) => (
        <div key={h.id} className="rounded-lg border p-3 space-y-1" data-testid={`ads-history-${h.id}`}>
          <p className="text-sm font-semibold">{h.mes && h.fase_id === 'all' ? `Ciclo mensual RRSS · ${monthName(h.mes)}` : h.fase_titulo} · {fmt(h.started_at)} → {fmt(h.closed_at)} <span className="text-muted-foreground font-normal">({h.done_count} de {h.total}, cerrado por {h.closed_by})</span></p>
          {h.items.map((i, k) => <p key={k} className={`text-xs ${i.hecho ? '' : 'text-muted-foreground'}`}>{i.hecho ? '✓' : '○'} {i.texto}</p>)}
        </div>
      ))}
    </DialogContent>
  </Dialog>
);

const MonthBar = ({ data, onMonth, onReset, onHistory }) => (
  <div className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/40 p-3" data-testid="checklist-month-bar">
    <span className="text-sm">Mes del ciclo:</span>
    <Input type="month" value={data.mes || ''} onChange={(e) => e.target.value && onMonth(e.target.value)} className="h-8 w-[170px]" data-testid="checklist-month-input" />
    <span className="text-xs text-muted-foreground">{monthName(data.mes)} · {data.n_posts_mes ?? 0} {(data.n_posts_mes ?? 0) === 1 ? 'publicación' : 'publicaciones'}</span>
    <div className="ml-auto flex gap-1">
      <Button variant="ghost" size="sm" className="h-8" onClick={onHistory} data-testid="checklist-history-btn"><History className="w-3.5 h-3.5 mr-1" />Meses anteriores</Button>
      <Button variant="outline" size="sm" className="h-8" onClick={onReset} data-testid="checklist-reset-all-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" />Cerrar mes y empezar el siguiente</Button>
    </div>
  </div>
);

export const ClientChecklist = ({ clientId, kind }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [hist, setHist] = useState(null);

  const run = useCallback(async (p, id) => {
    setBusy(id);
    try { setData(await p); } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  }, []);
  useEffect(() => { setData(null); run(checklistsAPI.get(kind, clientId)); }, [clientId, kind, run]);

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const lineales = data.phases.filter((p) => !p.recurrente);
  const done = lineales.reduce((a, p) => a + p.done_count, 0);
  const total = lineales.reduce((a, p) => a + p.items.length, 0);

  const resetPhase = (phase) => {
    if (!window.confirm(`¿Cerrar el ciclo de «${phase.titulo}»? Se guarda en el historial y las casillas vuelven a empezar.`)) return;
    run(checklistsAPI.resetPhase(kind, clientId, phase.id)).then(() => toast.success('Ciclo guardado y reiniciado'));
  };
  const resetAll = () => {
    if (!window.confirm(`¿Cerrar ${monthName(data.mes)}? Se guarda en el historial y el checklist empieza vacío para el mes siguiente.`)) return;
    run(checklistsAPI.resetAll(kind, clientId)).then(() => toast.success('Mes cerrado y guardado'));
  };

  return (
    <div className="space-y-3" data-testid={`checklist-${kind}`}>
      <div className="flex items-center gap-3">
        <Progress value={total ? (done / total) * 100 : 0} className="h-2 flex-1" />
        <span className="text-sm text-muted-foreground tabular-nums" data-testid={`checklist-${kind}-total`}>{done} de {total}</span>
      </div>
      {data.recurrente && <MonthBar data={data} onMonth={(m) => run(checklistsAPI.setMonth(kind, clientId, m))} onReset={resetAll} onHistory={() => setHist(data.history)} />}
      {data.phases.map((p) => (
        <AdsPhaseCard key={p.id} phase={p} busy={busy}
          onToggle={(id, v) => run(checklistsAPI.setItem(kind, clientId, id, v), id)}
          onAdd={(fid, t) => run(checklistsAPI.addCustom(kind, clientId, fid, t))}
          onDelete={(id) => run(checklistsAPI.delCustom(kind, clientId, id))}
          onReset={resetPhase}
          onHistory={(fid) => setHist(data.history.filter((h) => h.fase_id === fid))} />
      ))}
      <HistoryDialog entries={hist} onClose={() => setHist(null)} title={data.recurrente ? 'Meses anteriores' : 'Ciclos anteriores'} />
    </div>
  );
};
